import {
  OpportunityFormInput,
  OpportunityFragment,
  TradeIntentSizeType,
  TradeIntentStatusType,
  useCreateOrUpdateOpportunityMutation,
} from 'graphql/bindings/AdminBindings'
import {
  PencilAltIcon,
  SyncIcon,
  TimesCircleIcon,
  TrashIcon,
  useRouter,
  useSnackbar,
} from '@forgeglobal/design-framework'
import { PopoverListItem, PopoverListItemIcon, PopoverListItemText } from 'isolated-design-library'
import { Routes } from 'util/Routes'
import React, { Fragment } from 'react'

interface OpportunityActionsProps {
  opportunity: OpportunityFragment
  showDeleteOpportunityModal: () => void
  showCancellationModal: () => void
}

export const OpportunityActions: React.FC<OpportunityActionsProps> = ({
  opportunity,
  showDeleteOpportunityModal,
  showCancellationModal,
}) => {
  const { id, trade_intent, company } = opportunity
  const { history } = useRouter()
  const { enqueueSnackbar } = useSnackbar()

  const isLive = ![TradeIntentStatusType.Closed, TradeIntentStatusType.Cancelled].includes(
    opportunity?.status ?? TradeIntentStatusType.Pipeline
  )

  const dollarSizes = {
    max_dollar_amount: trade_intent.max_dollar_amount,
    min_dollar_amount: trade_intent.min_dollar_amount,
  }

  const sharesSizes = {
    min_num_shares: trade_intent.min_num_shares,
    max_num_shares: trade_intent.max_num_shares,
  }

  const formShape: OpportunityFormInput = {
    id,
    account_id: opportunity.account_id,
    company_id: company.id,
    type: opportunity.type,
    share_price: trade_intent.share_price,
    valuation: trade_intent.valuation,
    fee: trade_intent.fee,
    certainty: opportunity.certainty,
    renew: true,
  }

  const validFormShape =
    trade_intent.size_type === TradeIntentSizeType.Dollars
      ? { ...formShape, ...dollarSizes }
      : { ...formShape, ...sharesSizes }

  const [refreshOpportunity] = useCreateOrUpdateOpportunityMutation({
    variables: {
      form: validFormShape,
    },
    onCompleted: () => {
      enqueueSnackbar('Refreshed opportunity')
    },
  })
  const hasActiveDeals = opportunity.trade_intent.active_deals.length > 0
  const headerActions = (
    <Fragment>
      <PopoverListItem button onClick={() => history.push(Routes.opportunity(id))}>
        <PopoverListItemIcon>
          <PencilAltIcon />
        </PopoverListItemIcon>
        <PopoverListItemText>Edit</PopoverListItemText>
      </PopoverListItem>
      <PopoverListItem button onClick={() => refreshOpportunity()}>
        <PopoverListItemIcon>
          <SyncIcon />
        </PopoverListItemIcon>
        <PopoverListItemText>Refresh</PopoverListItemText>
      </PopoverListItem>
      {isLive && (
        <PopoverListItem button disabled={hasActiveDeals} onClick={showCancellationModal}>
          <PopoverListItemIcon>
            <TimesCircleIcon />
          </PopoverListItemIcon>
          <PopoverListItemText>Cancel</PopoverListItemText>
        </PopoverListItem>
      )}
      <PopoverListItem button disabled={hasActiveDeals} onClick={showDeleteOpportunityModal}>
        <PopoverListItemIcon>
          <TrashIcon />
        </PopoverListItemIcon>
        <PopoverListItemText>Delete</PopoverListItemText>
      </PopoverListItem>
    </Fragment>
  )

  return headerActions
}
